<template>
  <d-modal
    size="lg"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('shipments.popups.addShipment') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <div v-if="!allDataLoaded || saveInProgress" class="pb-3 pt-3 mx-0 my-0 text-center popup-overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>

      <d-form v-if="allDataLoaded && !loadError">
        <validation-observer ref="shipment-add-form" tag="form">
          <h4 class="mt-0">
            {{ $t('shipments.popups.receiverDataTitle') }}
          </h4>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.selectAddress') }}:
            </label>

            <div class="d-flex">
              <div :class="{
                'form-control': true,
                'is-select': true
              }">
                <v-select
                  :options="addresses"
                  v-model="address_id"
                  :searchable="true"
                  :reduce="address => address.id"
                  :placeholder="$t('order.chooseShippingAddress')"
                  label="name"
                  ref="customer-addresses-filter"
                  :filter="filteredAddresses"
                  @search="debouncedFetchAddresses">
                  <template slot="no-options">
                    {{ $t('order.noAddresses') }}
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      <template v-if="option.name">{{ option.name }}</template>
                      <template v-if="option.phone"> {{ $t('ui.phoneShort') }} {{ option.phone }} </template>
                      <template v-if="option.email"> @: {{ option.email }} </template>
                      <template v-if="option.name || option.phone || option.email"> - </template>
                      {{ option.company_name }} - {{ option.street }}
                      <template v-if="option.building">{{ option.building }} / {{ option.locale }}</template>
                      ({{ option.postcode }} {{ option.city }})
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      <template v-if="option.name">{{ option.name }}</template>
                      <template v-if="option.phone"> {{ $t('ui.phoneShort') }} {{ option.phone }} </template>
                      <template v-if="option.email"> @: {{ option.email }} </template>
                      <template v-if="option.name || option.phone || option.email"> - </template>
                      {{ option.company_name }} - {{ option.street }}
                      <template v-if="option.building">{{ option.building }} / {{ option.locale }}</template>
                      ({{ option.postcode }} {{ option.city }})
                    </div>
                  </template>
                </v-select>
              </div>

              <d-button
                v-if="address_id"
                theme="success"
                class="w-25 ml-3"
                @click.prevent="useAddress(address_id)">
                {{ $t('shipments.popups.useAddress') }}
              </d-button>
            </div>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.receiverName') }}:
            </label>

            <validation-provider
              rules="required|max:35"
              name="receiver_name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="receiver_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.receiverAttentionName') }}:
            </label>

            <validation-provider
              rules="required|max:35"
              name="receiver_attention_name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="receiver_attention_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverPhone') }}:
                </label>

                <validation-provider
                  rules="required|max:15"
                  name="receiver_phone"
                  v-slot="{ errors }">
                  <d-input
                    v-mask="'######################################'"
                    v-model="receiver_phone"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverEmail') }}:
                </label>

                <validation-provider
                  rules="email|max:50"
                  name="receiver_email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="receiver_email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="3">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverCountryCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="receiver_address_country_code"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="receiver_address_country_code"
                    :state="errors.length ? 'invalid' : null"
                    :options="countriesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverPostalCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="receiver_address_postal_code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="receiver_address_postal_code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <div class="mt-3">
                  <d-button
                    class="px-2 py-2 mt-3 w-100"
                    :outline="true"
                    :disabled="checkPostalCodeInProgress || !receiver_address_country_code || !receiver_address_postal_code"
                    @click.prevent="checkPostalCode">
                    <div
                      v-if="checkPostalCodeInProgress"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <template v-else>
                      {{ $t('shipments.popups.checkPostalCode') }}
                    </template>
                  </d-button>
                </div>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverCity') }}:
                </label>

                <validation-provider
                  rules="required|max:30"
                  name="receiver_address_city"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="receiver_address_city" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.receiverStreet') }}:
            </label>

            <validation-provider
              :rules="'required|max:' + maxReceiverLength"
              name="receiver_street"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="receiver_street" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverBuilding') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="receiver_building"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="receiver_building" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.receiverLocale') }}:
                </label>

                <d-input
                  v-model="receiver_locale" />
              </div>
            </d-col>
          </d-row>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.receiverLine2') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="receiver_line_2"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="receiver_address_line_2" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.receiverLine3') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="receiver_line_2"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="receiver_address_line_3" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <h4 class="mt-5">{{ $t('shipments.popups.shipperDataTitle') }}</h4>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.shipper') }}:
            </label>

            <d-row>
              <d-col cols="4">
                <validation-provider
                  rules="required"
                  name="shipper_id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="shipper_id"
                    :state="errors.length ? 'invalid' : null"
                    :options="shippersOptions"
                    @input="setShipperNumber" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </d-col>

              <d-col cols="8" class="d-flex">
                <d-button
                  @click.prevent="setShipperData"
                  theme="success"
                  :outline="true"
                  class="flex-grow-1">
                  {{ $t('shipments.popups.setShipper') }}
                </d-button>

                <d-button
                  v-if="customerData"
                  @click.prevent="setCustomerData"
                  theme="primary"
                  :outline="true"
                  class="flex-grow-1 ml-2">
                  {{ $t('shipments.popups.setCustomerData') }}
                </d-button>

                <d-button
                  @click.prevent="showShipperAddressSearch = !showShipperAddressSearch"
                  theme="info"
                  :outline="!showShipperAddressSearch"
                  class="ml-2"
                  :title="$t('shipments.popups.searchShipperAddress')">
                  <i class="material-icons">search</i>
                </d-button>
              </d-col>
            </d-row>

            <div
              v-if="showShipperAddressSearch"
              class="d-flex mt-2">
              <div :class="{
                'form-control': true,
                'is-select': true
              }">
                <v-select
                  :options="shipperAddresses"
                  v-model="shipper_address_id"
                  :searchable="true"
                  :reduce="address => address.id"
                  :placeholder="$t('shipments.popups.searchShipperAddressPlaceholder')"
                  label="name"
                  ref="shipper-addresses-filter"
                  :filter="filteredAddresses"
                  @search="debouncedFetchShipperAddresses">
                  <template slot="no-options">
                    {{ $t('order.noAddresses') }}
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      <template v-if="option.name">{{ option.name }}</template>
                      <template v-if="option.phone"> {{ $t('ui.phoneShort') }} {{ option.phone }} </template>
                      <template v-if="option.email"> @: {{ option.email }} </template>
                      <template v-if="option.name || option.phone || option.email"> - </template>
                      {{ option.company_name }} - {{ option.street }}
                      <template v-if="option.building">{{ option.building }} / {{ option.locale }}</template>
                      ({{ option.postcode }} {{ option.city }})
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      <template v-if="option.name">{{ option.name }}</template>
                      <template v-if="option.phone"> {{ $t('ui.phoneShort') }} {{ option.phone }} </template>
                      <template v-if="option.email"> @: {{ option.email }} </template>
                      <template v-if="option.name || option.phone || option.email"> - </template>
                      {{ option.company_name }} - {{ option.street }}
                      <template v-if="option.building">{{ option.building }} / {{ option.locale }}</template>
                      ({{ option.postcode }} {{ option.city }})
                    </div>
                  </template>
                </v-select>
              </div>

              <d-button
                v-if="shipper_address_id"
                theme="success"
                class="w-25 ml-3"
                @click.prevent="useShipperAddress(shipper_address_id)">
                {{ $t('shipments.popups.useAddress') }}
              </d-button>
            </div>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.shipperNumber') }}:
            </label>

            <validation-provider
              rules="required"
              name="shipper_number"
              v-slot="{ errors }">
              <d-input
                :disabled="true"
                :state="errors.length ? 'invalid' : null"
                v-model="shipper_number" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.shipperName') }}:
            </label>

            <validation-provider
              rules="required|max:35"
              name="shipper_name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="shipper_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.shipperAttentionName') }}:
            </label>

            <validation-provider
              rules="required|max:35"
              name="shipper_attention_name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="shipper_attention_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperPhone') }}:
                </label>

                <validation-provider
                  rules="required|max:15"
                  name="shipper_phone"
                  v-slot="{ errors }">
                  <d-input
                    v-mask="'#####################################'"
                    v-model="shipper_phone"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperEmail') }}:
                </label>

                <validation-provider
                  rules="email|max:50"
                  name="shipper_email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="shipper_email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="3">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperCountryCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="shipper_address_country_code"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="shipper_address_country_code"
                    :disabled="true"
                    :state="errors.length ? 'invalid' : null"
                    :options="countriesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="3">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperPostalCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="shipper_address_postal_code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="shipper_address_postal_code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperCity') }}:
                </label>

                <validation-provider
                  rules="required|max:30"
                  name="shipper_address_city"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="shipper_address_city" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.shipperStreet') }}:
            </label>

            <validation-provider
              :rules="'required|max:' + maxShipperLength"
              name="shipper_street"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="shipper_street" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <d-row>
            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperBuilding') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="shipper_building"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="shipper_building" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="6">
              <div class="form-group">
                <label>
                  {{ $t('shipments.popups.shipperLocale') }}:
                </label>

                <d-input
                  v-model="shipper_locale" />
              </div>
            </d-col>
          </d-row>

          <h4 class="mt-5">{{ $t('shipments.popups.serviceDataTitle') }}</h4>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.upsService') }}:
            </label>

            <validation-provider
              rules="required"
              name="service"
              v-slot="{ errors }">
              <d-form-select
                v-model="service"
                :state="errors.length ? 'invalid' : null"
                :options="servicesOptions" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.packageReferenceNumber') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="reference_number"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="reference_number" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.packageReferenceNumber') }} 2:
            </label>

            <validation-provider
              rules="max:35"
              name="reference_number_2"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="reference_number_2" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('shipments.popups.shipmentDescription') }}:
            </label>

            <validation-provider
              :rules="descriptionIsRequired ? 'required|max:50' : 'max:50'"
              name="description"
              v-slot="{ errors }">
              <d-input
                v-model="description"
                :state="errors.length ? 'invalid' : null" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <h4 class="d-flex mt-5">
            <span>{{ $t('shipments.popups.packagesDataTitle') }}</span>

            <d-button
              theme="success"
              @click.prevent="addPackage"
              class="ml-auto">
              {{ $t('shipments.popups.addPackage') }}
            </d-button>
          </h4>

          <div class="form-group">
            <table class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0" style="width: 120px;">{{ $t('shipments.popups.packageWeight') }}</th>
                  <th scope="col" class="border-0">{{ $t('shipments.popups.packageReferenceNumber') }}</th>
                  <th scope="col" class="border-0">{{ $t('shipments.popups.packageDescription') }}</th>
                  <th scope="col" class="border-0" style="width: 140px;">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="packages.length">
                <tr
                  v-for="(packageItem, index) of packages"
                  :key="'package-' + index">
                  <td class="align-top">
                    <validation-provider
                      rules="required|min_value:0.01"
                      :name="'package_weight_' + index"
                      v-slot="{ errors }">
                      <input
                        :class="{
                          'form-control': true,
                          'is-invalid': errors.length
                        }"
                        :invalid="errors.length ? true : null"
                        type="number"
                        v-model="packageItem.weight" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </td>
                  <td class="align-top">
                    <validation-provider
                      rules="max:35"
                      :name="'package_reference_number_' + index"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="packageItem.reference_number" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>

                    <validation-provider
                      rules="max:35"
                      :name="'package_reference_number_2_' + index"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="packageItem.reference_number_2"
                        class="mt-2" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </td>
                  <td class="align-top">
                    <validation-provider
                      rules="max:35"
                      :name="'package_description_' + index"
                      v-slot="{ errors }">
                      <d-input
                        v-model="packageItem.description"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </td>
                  <td class="align-top">
                    <d-button
                      theme="primary"
                      @click.prevent="clonePackage(index)">
                      <i class="material-icons">content_copy</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      class="ml-2"
                      @click.prevent="removePackage(index)">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="4" class="text-center">
                    {{ $t('shipments.popups.noPackages') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 class="mt-5">{{ $t('shipments.popups.paymentDataTitle') }}</h4>

          <div class="form-group">
            <d-checkbox
              inline
              v-model="is_cod"
              toggle
              toggle-small>
              {{ $t('shipments.popups.isCOD') }}
            </d-checkbox>
          </div>

          <d-row>
            <d-col cols="4">
              <div
                v-if="is_cod"
                class="form-group">
                <label>
                  {{ $t('shipments.popups.codCurrencyCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="cod_currency_code"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="cod_currency_code"
                    :state="errors.length ? 'invalid' : null"
                    :options="currenciesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>

            <d-col cols="8">
              <div
                v-if="is_cod"
                class="form-group">
                <label>
                  {{ $t('shipments.popups.codAmount') }}:
                </label>

                <validation-provider
                  rules="required|min:0"
                  name="cod_amoumt"
                  v-slot="{ errors }">
                  <input
                    :class="{
                      'form-control': true,
                      'is-invalid': errors.length
                    }"
                    :invalid="errors.length ? true : null"
                    type="number"
                    v-model="cod_amount" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel"
        :disabled="saveInProgress">
        {{ $t('ui.cancel') }}
      </d-button>

      <div class="ml-auto mr-3">
        <template v-if="allDataLoaded && !loadError">
          <strong>{{ $t('shipments.popups.estimatedDeliveryDate') }}:</strong>

          <div
            v-if="estimateDeliveryInProgress"
            class="spinner-border spinner-border-sm ml-1"
            role="status"></div>
          <template v-else-if="estimatedDeliveryDate">
            {{ estimatedDeliveryDate }}
          </template>
          <span
            v-else-if="!canEstimateDelivery"
            class="text-danger">
            {{ $t('shipments.popups.estimatedDeliveryDateFillData') }}
          </span>
          <span
            v-else-if="!service"
            class="text-danger">
            {{ $t('shipments.popups.estimatedDeliveryDateSelectService') }}
          </span>
          <span
            v-else
            class="text-danger">
            {{ $t('shipments.popups.estimatedDeliveryDateError') }}
          </span>
        </template>
      </div>

      <d-button
        class="ml-0"
        theme="success"
        @click.prevent="validateForm"
        :disabled="saveInProgress">
        {{ $t('shipments.popups.saveShipment') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import debounce from 'lodash.debounce';
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import { mapGetters } from 'vuex';
import Vue from 'vue';

export default {
  name: 'shipment-add-popup',
  props: [
    'orderID',
    'orderName',
    'addressData',
    'customerID',
    'customerData',
    'customerContactData',
    'contactData',
    'orderDescription'
  ],
  computed: {
    ...mapGetters([
      'getUserName'
    ]),
    allDataLoaded () {
      return this.loadedSettings && this.loadedCountries && this.loadedCurrencies;
    },
    countriesOptions () {
      return [{
        text: this.$t('shipments.popups.selectCountry'),
        value: null
      }].concat(CommonGetters.getCountriesForSelect(this.countries));
    },
    currenciesOptions () {
      return [{
        text: this.$t('shipments.popups.selectCurrency'),
        value: null
      }].concat(CommonGetters.getCurrenciesForSelect(this.currencies));
    },
    descriptionIsRequired () {
      return this.service === '65';
    },
    servicesOptions () {
      if (this.settingsData && this.settingsData.services) {
        return [{
          text: this.$t('shipments.popups.selectService'),
          value: null
        }].concat(this.settingsData.services.map((service) => ({
          text: service.name,
          value: service.code
        })));
      }

      return [];
    },
    shippersOptions () {
      if (this.settingsData && this.settingsData.shippers) {
        return [{
          text: this.$t('shipments.popups.selectShipper'),
          value: null
        }].concat(this.settingsData.shippers.map((shipper) => ({
          text: shipper.number_name + ' (' + shipper.number + ')',
          value: shipper.number
        })));
      }

      return [];
    },
    orderDescriptionFirstLine () {
      if (this.orderName) {
        return (this.orderName).toString().substr(0, 35);
      }

      return '';
    },
    receiverCountryAndPostCode () {
      return this.receiver_address_country_code + '|' + this.receiver_address_postal_code;
    },
    maxReceiverLength () {
      let maxLen = 35;

      if (this.receiver_building && this.receiver_building.length) {
        maxLen -= 1;
        maxLen -= this.receiver_building.length;
      }

      if (this.receiver_locale && this.receiver_locale.length) {
        maxLen -= 3;
        maxLen -= this.receiver_locale.length;
      }

      return maxLen;
    },
    maxShipperLength () {
      let maxLen = 35;

      if (this.shipper_building && this.shipper_building.length) {
        maxLen -= 1;
        maxLen -= this.shipper_building.length;
      }

      if (this.shipper_locale && this.shipper_locale.length) {
        maxLen -= 3;
        maxLen -= this.shipper_locale.length;
      }

      return maxLen;
    },
    estimateDeliveryData () {
      return {
        shipper_address_country_code: this.getCountryCodeByID(this.shipper_address_country_code),
        shipper_address_postal_code: this.shipper_address_postal_code,
        shipper_address_city: this.shipper_address_city,
        receiver_address_country_code: this.getCountryCodeByID(this.receiver_address_country_code),
        receiver_address_postal_code: this.receiver_address_postal_code,
        receiver_address_city: this.receiver_address_city,
        packages: this.packages
          .filter(packageItem => parseFloat(packageItem.weight) >= 0.01)
          .map(packageItem => ({
            weight: parseFloat(packageItem.weight)
          }))
      };
    },
    estimateDeliveryDataKey () {
      return JSON.stringify(this.estimateDeliveryData);
    },
    canEstimateDelivery () {
      let data = this.estimateDeliveryData;

      return !!data.shipper_address_country_code &&
        !!data.shipper_address_postal_code &&
        !!data.receiver_address_country_code &&
        !!data.receiver_address_postal_code &&
        this.packages.length > 0;
    },
    estimatedDeliveryDate () {
      if (!this.service) {
        return '';
      }

      let serviceToUse = this.estimatedDeliveryServices[this.service];

      if (serviceToUse && serviceToUse.delivery_date) {
        return serviceToUse.delivery_date;
      }

      return '';
    }
  },
  watch: {
    receiverCountryAndPostCode () {
      this.post_code_checked = false;
      this.warning_shown = false;
    },
    estimateDeliveryDataKey () {
      this.resetEstimatedDelivery();
      this.debouncedEstimateDelivery();
    }
  },
  data () {
    return {
      shipper_id: null,
      shipper_number: '',
      shipper_name: '',
      shipper_attention_name: '',
      shipper_phone: '',
      shipper_email: '',
      shipper_address_country_code: null,
      shipper_address_postal_code: '',
      shipper_address_city: '',
      shipper_street: '',
      shipper_building: '',
      shipper_locale: '',
      receiver_name: '',
      receiver_attention_name: '',
      receiver_phone: '',
      receiver_email: '',
      receiver_address_country_code: null,
      receiver_address_postal_code: '',
      receiver_address_city: '',
      receiver_street: '',
      receiver_building: '',
      receiver_locale: '',
      receiver_address_line_2: '',
      receiver_address_line_3: '',
      service: 11,
      is_cod: false,
      reference_number: '',
      reference_number_2: '',
      cod_amount: '',
      cod_currency_code: null,
      description: '',
      packages: [],
      post_code_checked: false,
      warning_shown: false,
      // helper data
      countries: [],
      currencies: [],
      settingsData: [],
      addresses: [],
      address_id: null,
      shipperAddresses: [],
      shipper_address_id: null,
      estimatedDeliveryServices: {},
      lastEstimateDeliveryRequestID: 0,
      // UI flags
      loadError: false,
      loadedCountries: false,
      loadedCurrencies: false,
      loadedSettings: false,
      saveInProgress: false,
      loadAddressesError: false,
      loadShipperAddressesError: false,
      checkPostalCodeInProgress: false,
      showShipperAddressSearch: false,
      estimateDeliveryInProgress: false
    };
  },
  mounted () {
    this.loadAdditionalData();

    if (this.addressData) {
      this.receiver_name = this.addressData.receiver_name;
      this.receiver_address_country_code = this.addressData.receiver_address_country_code;
      this.receiver_address_postal_code = this.addressData.receiver_address_postal_code;
      this.receiver_address_city = this.addressData.receiver_address_city;
      this.receiver_street = this.addressData.receiver_address_street;
      this.receiver_building = this.addressData.receiver_address_building;
      this.receiver_locale = this.addressData.receiver_address_locale;
      this.receiver_address_line_2 = this.addressData.receiver_address_line_2;
      this.receiver_address_line_3 = this.addressData.receiver_address_line_3;
    }

    if (this.contactData) {
      this.receiver_attention_name = this.contactData.receiver_attention_name;
      this.receiver_phone = this.contactData.receiver_phone;
      this.receiver_email = this.contactData.receiver_email;
    }

    this.reference_number = this.orderDescriptionFirstLine;
    this.fetchAddresses();
  },
  methods: {
    handleClose () {
      this.$bus.$emit('add-shipment-popup-close');
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/shipments/settings',
        loadedKey: 'loadedSettings',
        outputKey: 'settingsData',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError',
        successAction: () => {
          let poland = this.countries.filter(country => country.code === 'PL');

          if (poland.length) {
            this.shipper_address_country_code = poland[0].id;
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['shipment-add-form'], this.saveShipment);
    },
    saveShipment () {
      let shipperToSet = null;

      if (this.estimateDeliveryInProgress) {
        Vue.swal({
          title: this.$t('shipments.popups.estimateDeliveryInProgressTitle'),
          html: this.$t('shipments.popups.estimateDeliveryInProgressDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.saveInProgress = true;

      if (this.shipper_id) {
        shipperToSet = this.settingsData.shippers.filter(shipper => shipper.number === this.shipper_id);

        if (!shipperToSet || !shipperToSet.length) {
          shipperToSet = null;
        }
      }

      if (!shipperToSet) {
        Vue.swal({
          title: this.$t('shipments.popups.invalidShipper'),
          html: this.$t('shipments.popups.invalidShipperDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      if (!this.packages.length) {
        Vue.swal({
          title: this.$t('shipments.popups.emptyPackages'),
          html: this.$t('shipments.popups.emptyPackagesDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      let receiverCountryCode = null;
      receiverCountryCode = this.countries.filter(country => country.id === this.receiver_address_country_code);

      if (receiverCountryCode[0]) {
        receiverCountryCode = receiverCountryCode[0].address_code;
      } else {
        receiverCountryCode = '';
      }

      let shipperCountryCode = 'PL';

      let dataToSend = {
        shipper_number: this.shipper_number,
        shipper_name: this.shipper_name,
        shipper_attention_name: this.shipper_attention_name,
        shipper_phone: this.shipper_phone,
        shipper_email: this.shipper_email,
        shipper_address_country_code: shipperCountryCode,
        shipper_address_postal_code: this.shipper_address_postal_code,
        shipper_address_city: this.shipper_address_city,
        shipper_street: this.shipper_street,
        shipper_building: this.shipper_building,
        shipper_locale: this.shipper_locale,
        receiver_name: this.receiver_name,
        receiver_attention_name: this.receiver_attention_name,
        receiver_phone: this.receiver_phone,
        receiver_email: this.receiver_email,
        receiver_address_country_code: receiverCountryCode,
        receiver_address_postal_code: this.receiver_address_postal_code,
        receiver_address_city: this.receiver_address_city,
        receiver_street: this.receiver_street,
        receiver_building: this.receiver_building,
        receiver_locale: this.receiver_locale,
        receiver_address_line_2: this.receiver_address_line_2,
        receiver_address_line_3: this.receiver_address_line_3,
        service: this.service,
        is_cod: +this.is_cod,
        description: this.description,
        packages: this.packages,
        reference_number: this.reference_number,
        reference_number_2: this.reference_number_2,
        post_code_checked: +this.post_code_checked,
        warning_shown: +this.warning_shown,
        estimate_delivery_date: this.estimatedDeliveryDate || null
      };

      if (this.orderID) {
        dataToSend.order_id = this.orderID;
      }

      if (this.is_cod) {
        let codCurrencyCode = this.currencies.filter(currency => currency.id === this.cod_currency_code);
        codCurrencyCode = codCurrencyCode[0].code;
        dataToSend.cod_amount = this.cod_amount;
        dataToSend.cod_currency_code = codCurrencyCode;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/shipments/store',
        formData: dataToSend,
        successTitle: this.$t('shipments.popups.saveSuccessTitle'),
        successText: this.$t('shipments.popups.saveSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('shipments.popups.saveErrorText'),
        successAction: () => {
          this.$bus.$emit('shipments-reload');
          this.$bus.$emit('order-new-shipment-added');
          this.$bus.$emit('contacts-refresh-list');
          this.saveInProgress = false;
          this.handleClose();
        },
        failAction: () => {
          this.saveInProgress = false;
        }
      });
    },
    addPackage () {
      this.packages.push({
        weight: 10,
        reference_number: this.orderDescriptionFirstLine,
        reference_number_2: '',
        description: ''
      });
    },
    removePackage (packageIndex) {
      this.packages.splice(packageIndex, 1);
    },
    clonePackage (index) {
      Vue.swal({
        title: this.$t('shipments.popups.bulkCloneTitle'),
        text: this.$t('shipments.popups.bulkCloneText'),
        type: 'question',
        input: 'number',
        inputPlaceholder: this.$t('shipments.popups.howManyPackages'),
        inputValue: '',
        inputValidator: (value) => new Promise((resolve) => {
          if ((value).toString().replace(/[^0-9]/gmi, '') === (value).toString()) {
            resolve();
            return;
          }

          resolve(this.$t('shipments.popups.bulkCloneError'));
        }),
        showCancelButton: true,
        confirmButtonText: this.$t('shipments.popups.bulkCloneAdd'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          let copy = JSON.parse(JSON.stringify(this.packages[index]));
          let copiesCount = parseInt(result.value, 10);

          for (let i = 0; i < copiesCount; i++) {
            this.packages.push(copy);
          }
        }
      });
    },
    cancel () {
      this.handleClose();
    },
    setShipperNumber () {
      let shipperToSet = null;

      if (this.shipper_id) {
        shipperToSet = this.settingsData.shippers.filter(shipper => shipper.number === this.shipper_id);

        if (!shipperToSet || !shipperToSet.length) {
          shipperToSet = null;
        } else {
          Vue.set(this, 'shipper_number', shipperToSet[0].number);
        }
      } else {
        Vue.set(this, 'shipper_number', '');
      }
    },
    setShipperData () {
      let shipperToSet = null;

      if (this.shipper_id) {
        shipperToSet = this.settingsData.shippers.filter(shipper => shipper.number === this.shipper_id);

        if (!shipperToSet || !shipperToSet.length) {
          shipperToSet = null;
        }
      }

      if (!shipperToSet) {
        Vue.swal({
          title: this.$t('shipments.popups.invalidShipper'),
          html: this.$t('shipments.popups.invalidShipperDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      Vue.set(this, 'shipper_name', shipperToSet[0].name);
      Vue.set(this, 'shipper_attention_name', this.getUserName);
      Vue.set(this, 'shipper_phone', shipperToSet[0].phone);
      Vue.set(this, 'shipper_email', shipperToSet[0].email);
      Vue.set(this, 'shipper_address_postal_code', shipperToSet[0].address_postal_code);
      Vue.set(this, 'shipper_address_city', shipperToSet[0].address_city);
      Vue.set(this, 'shipper_street', shipperToSet[0].address_street);
      Vue.set(this, 'shipper_building', shipperToSet[0].address_building);
      Vue.set(this, 'shipper_locale', shipperToSet[0].address_locale);
    },
    setCustomerData () {
      if (!this.customerData) {
        return;
      }

      Vue.set(this, 'shipper_name', this.customerData.company_name);

      if (this.customerContactData) {
        Vue.set(this, 'shipper_attention_name', this.customerContactData.name);
        Vue.set(this, 'shipper_phone', this.customerContactData.phone);
        Vue.set(this, 'shipper_email', this.customerContactData.email);
      }

      Vue.set(this, 'shipper_address_postal_code', this.customerData.address_postal_code);
      Vue.set(this, 'shipper_address_city', this.customerData.address_city);
      Vue.set(this, 'shipper_street', this.customerData.address_street);
      Vue.set(this, 'shipper_building', this.customerData.address_building);
      Vue.set(this, 'shipper_locale', this.customerData.address_locale);
    },
    // eslint-disable-next-line
    debouncedFetchAddresses: debounce(function (searchPhrase, loadingCallback) {
      this.fetchAddresses(searchPhrase, loadingCallback);
    }, 500),
    fetchAddresses (searchPhrase, loadingCallback) {
      if (!this.customerID && (!searchPhrase || searchPhrase.length < 4)) {
        return;
      }

      if (loadingCallback) {
        loadingCallback(true);
      }

      FormUtils.loadAdditionalData(this, {
        method: 'post',
        endpoint: '/customers/addresses/search',
        outputKey: 'addresses',
        errorKey: 'loadAddressesError',
        params: {
          customer_id: this.customerID,
          search: searchPhrase
        },
        successAction: () => {
          if (loadingCallback) {
            loadingCallback(false);
          }
        }
      });
    },
    // eslint-disable-next-line
    debouncedFetchShipperAddresses: debounce(function (searchPhrase, loadingCallback) {
      this.fetchShipperAddresses(searchPhrase, loadingCallback);
    }, 500),
    fetchShipperAddresses (searchPhrase, loadingCallback) {
      if (!searchPhrase || searchPhrase.length < 4) {
        return;
      }

      if (loadingCallback) {
        loadingCallback(true);
      }

      FormUtils.loadAdditionalData(this, {
        method: 'post',
        endpoint: '/customers/addresses/search',
        outputKey: 'shipperAddresses',
        errorKey: 'loadShipperAddressesError',
        params: {
          search: searchPhrase
        },
        successAction: () => {
          if (loadingCallback) {
            loadingCallback(false);
          }
        }
      });
    },
    useShipperAddress (addressID) {
      let addressToUse = this.shipperAddresses.filter(address => address.id === addressID);

      if (addressToUse && addressToUse[0]) {
        Vue.set(this, 'shipper_name', addressToUse[0].company_name);
        Vue.set(this, 'shipper_attention_name', addressToUse[0].name);
        Vue.set(this, 'shipper_phone', addressToUse[0].phone);
        Vue.set(this, 'shipper_email', addressToUse[0].email);
        Vue.set(this, 'shipper_address_postal_code', addressToUse[0].postcode);
        Vue.set(this, 'shipper_address_city', addressToUse[0].city);
        Vue.set(this, 'shipper_street', addressToUse[0].street);
        Vue.set(this, 'shipper_building', addressToUse[0].building);
        Vue.set(this, 'shipper_locale', addressToUse[0].locale);
      }
    },
    // eslint-disable-next-line
    debouncedEstimateDelivery: debounce(function () {
      this.estimateDelivery();
    }, 500),
    resetEstimatedDelivery () {
      this.lastEstimateDeliveryRequestID++;
      this.estimatedDeliveryServices = {};
      this.estimateDeliveryInProgress = this.canEstimateDelivery;
    },
    estimateDelivery () {
      this.lastEstimateDeliveryRequestID++;
      let requestID = this.lastEstimateDeliveryRequestID;

      if (!this.canEstimateDelivery) {
        this.estimatedDeliveryServices = {};
        this.estimateDeliveryInProgress = false;
        return;
      }

      this.estimateDeliveryInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/shipments/estimate-delivery',
        formData: this.estimateDeliveryData,
        skipErrorDisplay: true,
        successAction: (response) => {
          if (requestID !== this.lastEstimateDeliveryRequestID) {
            return;
          }

          let services = {};

          if (response && response.success && response.services) {
            services = response.services;
          }

          this.estimatedDeliveryServices = services;
          this.estimateDeliveryInProgress = false;
        },
        failAction: () => {
          if (requestID !== this.lastEstimateDeliveryRequestID) {
            return;
          }

          this.estimatedDeliveryServices = {};
          this.estimateDeliveryInProgress = false;
        }
      });
    },
    filteredAddresses (options, search) {
      return options.filter(option => this.filterByForAddress(option, option.id, search)).slice(0, 100);
    },
    filterByForAddress (option, label, search) {
      return (option.name + ' - ' + option.phone + ' - ' + option.email + ' - ' + option.company_name + ' - ' + option.street + ' ' + option.building + ' / ' + option.locale + ' (' + option.postcode + ' ' + option.city + ')').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1;
    },
    useAddress (addressID) {
      let addressToUse = this.addresses.filter(address => address.id === addressID);

      if (addressToUse && addressToUse[0]) {
        this.receiver_name = addressToUse[0].company_name;
        this.receiver_attention_name = addressToUse[0].name;
        this.receiver_phone = addressToUse[0].phone;
        this.receiver_email = addressToUse[0].email;
        this.receiver_address_country_code = addressToUse[0].country_id;
        this.receiver_address_postal_code = addressToUse[0].postcode;
        this.receiver_address_city = addressToUse[0].city;
        this.receiver_street = addressToUse[0].street;
        this.receiver_building = addressToUse[0].building;
        this.receiver_locale = addressToUse[0].locale;
        this.receiver_address_line_2 = addressToUse[0].line_2 || '';
        this.receiver_address_line_3 = addressToUse[0].line_3 || '';
      }
    },
    checkPostalCode () {
      this.checkPostalCodeInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/shipments/check-post-code',
        formData: {
          country_code: this.getCountryCodeByID(this.receiver_address_country_code),
          post_code: this.receiver_address_postal_code
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          if (response.post_code_exists) {
            if (response.other_codes) {
              Vue.swal({
                title: this.$t('shipments.popups.postCodeMultipleExistsTitle'),
                html: this.$t('shipments.popups.postCodeMultipleExistsDesc') + '<br>' + response.other_codes.map(item => this.getCountryNameByCode(item.country_code)).join(', '),
                type: 'success',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            } else {
              Vue.swal({
                title: this.$t('shipments.popups.postCodeExistsTitle'),
                html: this.$t('shipments.popups.postCodeExistsDesc'),
                type: 'success',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            }

            this.post_code_checked = true;
            this.warning_shown = false;
          } else {
            Vue.swal({
              title: this.$t('shipments.popups.postCodeNotExistsTitle'),
              html: this.$t('shipments.popups.postCodeNotExistsDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.post_code_checked = true;
            this.warning_shown = true;
          }

          this.checkPostalCodeInProgress = false;
        },
        failAction: () => {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.checkPostalCodeInProgress = false;
        }
      });
    },
    getCountryCodeByID (countryID) {
      let foundedCountry = this.countries.filter(country => country.id === countryID);

      if (foundedCountry && foundedCountry[0]) {
        return foundedCountry[0].address_code;
      }

      return '';
    },
    getCountryNameByCode (countryCode) {
      let foundedCountry = this.countries.filter(country => country.address_code === countryCode);

      if (foundedCountry && foundedCountry[0]) {
        return foundedCountry[0].name;
      }

      return countryCode;
    }
  }
}
</script>
